import React from 'react';
import classnames from 'classnames';
import { Button, Form, Input, Label, Modal, ModalBody, ModalHeader } from '../../../components/Bootstrap';
import { CenterModel } from '../types';
import {
  FufillmentCentersQuery_fulfillmentCenters as FufillmentCenter,
  FufillmentCentersQuery_deliveryConfigurations,
} from '../__generated__/FufillmentCentersQuery';
import { DeliveryValueRow } from './DeliveryValueRow';
import config from '../../../config';
import { QRCodeCanvas } from 'qrcode.react';
import { FormGroup } from 'react-bootstrap';

type LinkLocation = 'menu' | 'fulfillment-centers';

export interface QRCodeModalProps {
  open: boolean;
  onSubmit(data: CenterModel);
  onCancel();
  venueId: string;
  fulfillmentCenter: FufillmentCenter;
  deliveryConfigurations: FufillmentCentersQuery_deliveryConfigurations[];
}

const createUrl = (
  location: LinkLocation,
  venueId: string,
  fulfillmentCenterId: string,
  fields: { [key: string]: string }
) => {
  const consumerUrl = config.CONSUMER_URL;
  const deliveryLocation = btoa(JSON.stringify(fields));
  if (location === 'menu') {
    return `${consumerUrl}/venue/${venueId}/fulfillment-center/${fulfillmentCenterId}/menu?deliveryLocation=${deliveryLocation}`;
  } else {
    return `${consumerUrl}/venue/${venueId}?deliveryLocation=${deliveryLocation}`;
  }
};

function downloadStringAsFile(data: string, filename: string) {
  let a = document.createElement('a');
  a.download = filename;
  a.href = data;
  a.click();
}

export const QRCodeModal: React.FC<QRCodeModalProps> = ({ ...props }) => {
  const { fulfillmentCenter, deliveryConfigurations } = props;
  const [qrCodeUrl, setQrCodeUrl] = React.useState<string | null>('');
  const [linkLocation, setLinkLocation] = React.useState<LinkLocation>('menu');

  const deliveryFields = React.useMemo(() => {
    return deliveryConfigurations.find((c) => c.id === fulfillmentCenter.deliveryConfigurationId)?.deliveryFields;
  }, [deliveryConfigurations, fulfillmentCenter]);

  const [deliveryValues, setDeliveryValues] = React.useState<{ [key: string]: string }>({});
  const onSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setQrCodeUrl(createUrl(linkLocation, props.venueId, fulfillmentCenter.id, deliveryValues));
  };

  const onChangeDeliveryValue = React.useCallback(
    (key: string, newValue: string) => {
      setDeliveryValues({
        ...deliveryValues,
        [key]: newValue,
      });
    },
    [deliveryValues, setDeliveryValues]
  );

  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  const onCanvasButtonClick = () => {
    const node = canvasRef.current;
    if (node == null) {
      return;
    }
    // For canvas, we just extract the image data and send that directly.
    const dataURI = node.toDataURL('image/png');

    downloadStringAsFile(dataURI, 'qrcode-canvas.png');
  };

  const onClickCreateNew = () => {
    setQrCodeUrl(null);
  };

  return (
    <Modal size='lg' isOpen={props.open} toggle={props.onCancel}>
      <ModalHeader className='justify-content-center'>Create QR Code</ModalHeader>
      <ModalBody>
        {!qrCodeUrl && (
          <Form onSubmit={onSubmit}>
            <FormGroup className='fulfillment-center-modal-input mb-3'>
              <Label>QR Code Page Link</Label>
              <Input
                type='select'
                value={linkLocation}
                onChange={(e) => setLinkLocation(e.target.value)}
                required={true}
              >
                <option value={'menu' as LinkLocation}>Menu</option>
                <option value={'fulfillment-centers' as LinkLocation}>Fulfillment Centers</option>
              </Input>
            </FormGroup>
            {deliveryFields?.map((field) => (
              <DeliveryValueRow
                key={field.key}
                field={field}
                value={deliveryValues[field.key] ?? ''}
                onChange={onChangeDeliveryValue}
              />
            ))}
            <div className='text-center fulfillment-center-modal-btns'>
              <Button
                type='button'
                color='link'
                className={classnames('fulfillment-center-modal-cancel')}
                onClick={props.onCancel}
              >
                Cancel
              </Button>{' '}
              <Button type='submit' color='primary' className='btn--rounded-padded fulfillment-center-modal-submit'>
                Create
              </Button>
            </div>
          </Form>
        )}
        {qrCodeUrl && (
          <div className='text-center'>
            <div>
              <QRCodeCanvas
                id='delivery-qr-code'
                value={qrCodeUrl}
                size={290}
                level={'H'}
                includeMargin={true}
                {...{ ref: canvasRef }}
              />
            </div>
            <Button color='link' className='me-4' onClick={onClickCreateNew}>
              Create New
            </Button>
            <Button color='primary' onClick={onCanvasButtonClick}>
              Download
            </Button>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};
